/**
 * Cloud productions for the views (PLAN §9, §10.2 screen 5, D11): the list
 * with the network they run in (egress IPs, task sizes and prices), one
 * production, launch and stop. The data-hook pattern (`rundowns.ts`).
 *
 * **Polling.** A cloud production moves on by itself — `pending` → `starting`
 * → `running` as Fargate places the task and its node enrols, `stopping` →
 * `stopped` as it drains — and nothing pushes those changes to the portal, so
 * the list re-reads every `CLOUD_POLL_MS` while any production is in one of
 * those states, and stops polling once none is.
 *
 * A launch carries a client-minted `productionId`, so a retry (the failover
 * client resends it) returns the production it already made.
 */
import type { ApiClient } from '@norna/api-client/client.js';
import type { CloudProductionWire, CloudProductionNetworkWire } from '@norna/api-contract/cloudProductions.js';
import { type QueryClient, queryOptions } from '@tanstack/react-query';
import { type CloudLaunchRequest, isTransitional } from '../lib/cloudProductions.js';
import { productionKeys } from './productions.js';

export const cloudProductionKeys = {
  all: ['cloud-productions'] as const,
  list: () => [...cloudProductionKeys.all, 'list'] as const,
  one: (productionId: string) => [...cloudProductionKeys.all, 'one', productionId] as const,
};

/** How often the list is re-read while a production is pending, starting or stopping. */
export const CLOUD_POLL_MS = 3_000;

export type CloudProductionList = { readonly cloudProductions: readonly CloudProductionWire[]; readonly network: CloudProductionNetworkWire };

/** The poll interval for a list as last read: `CLOUD_POLL_MS` while any production is on the move, else none. */
export function cloudPollInterval(list: CloudProductionList | undefined): number | false {
  return list !== undefined && list.cloudProductions.some((p) => isTransitional(p.status)) ? CLOUD_POLL_MS : false;
}

/** Every cloud production the caller may read (newest first) and the network they run in. */
export function cloudProductionsQuery(api: ApiClient) {
  return queryOptions({
    queryKey: cloudProductionKeys.list(),
    queryFn: async (): Promise<CloudProductionList> => api.call('listCloudProductions', { params: null, query: {}, body: null }),
    refetchInterval: (query) => cloudPollInterval(query.state.data),
  });
}

/** `newId`: a ULID factory (the production's id is the client's, for an idempotent retry). */
export function launchCloudProductionMutation(api: ApiClient, queryClient: QueryClient, newId: () => string) {
  return {
    mutationFn: async (input: CloudLaunchRequest): Promise<CloudProductionWire> =>
      (await api.call('launchCloudProduction', { params: null, query: null, body: { productionId: newId(), ...input } })).cloudProduction,
    onSuccess: async (launched: CloudProductionWire): Promise<void> => {
      queryClient.setQueryData(cloudProductionKeys.one(launched.productionId), launched);
      // A launch also made the ordinary production record (it shows in the productions list).
      await Promise.all([queryClient.invalidateQueries({ queryKey: cloudProductionKeys.list() }), queryClient.invalidateQueries({ queryKey: productionKeys.list() })]);
    },
  };
}

export function stopCloudProductionMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly productionId: string; readonly reason: string | null }): Promise<CloudProductionWire> =>
      (await api.call('stopCloudProduction', { params: { productionId: input.productionId }, query: null, body: { reason: input.reason } })).cloudProduction,
    onSuccess: async (stopped: CloudProductionWire): Promise<void> => {
      queryClient.setQueryData(cloudProductionKeys.one(stopped.productionId), stopped);
      await Promise.all([queryClient.invalidateQueries({ queryKey: cloudProductionKeys.list() }), queryClient.invalidateQueries({ queryKey: productionKeys.list() })]);
    },
  };
}
